import React from 'react';
import { Button, makeStyles, mergeClasses, shorthands } from '@fluentui/react-components';
import { ReactComponent as SuggestedPromptsIcon } from '../../../../../assets/toolkit/suggested-prompts.svg';
import { customColors, customFonts } from '../../../../../styles';
import type { SuggestedPromptCategoryOption } from '../../../hooks/useSuggestedPrompts';
import type { UseSavedPromptVm, UserPromptOption } from '../../../hooks/useSavedPrompt';
import useDismissOnOutsideClick from '../../../hooks/useDismissOnOutsideClick';
import SuggestedPromptsMenu from './SuggestedPromptsMenu';
import SavedPromptDialogs from './SavedPromptDialogs';

const trayButtonIcon = '& .toolkitTrayIcon';
const trayButtonIconSvg = '& .toolkitTrayIcon svg';

const useStyles = makeStyles({
    suggestedPromptsWrapper: {
        position: 'relative',
    },
    trayButton: {
        height: '32px',
        fontFamily: customFonts.Lato,
        fontSize: '14px',
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '12px'),
        backgroundColor: customColors.white,
        whiteSpace: 'nowrap',
        transition: 'all 0.2s ease',

        [trayButtonIconSvg]: {
            color: customColors.protivitiDarkBlue,
            transition: 'all 0.2s ease',
        },

        '&:hover': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
            ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),

            [trayButtonIcon]: {
                backgroundColor: 'transparent',
            },

            [trayButtonIconSvg]: {
                color: customColors.white,
            },
        },

        '&:active': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
            transition: 'all 0s',

            [trayButtonIcon]: {
                backgroundColor: 'transparent',
            },

            [trayButtonIconSvg]: {
                color: customColors.white,
            },
        },
    },
    trayButtonActive: {
        backgroundColor: customColors.protivitiDarkBlue,
        color: customColors.white,

        [trayButtonIconSvg]: {
            color: customColors.white,
        },

        '&:hover': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
        },
    },
});

interface SuggestedPromptsTrayItemProps {
    isOpen: boolean;
    isDropUp: boolean;
    expandCategoriesByDefault: boolean;
    userPrompts: UserPromptOption[];
    suggestedPromptCategories: SuggestedPromptCategoryOption[];
    savedPromptVm: UseSavedPromptVm;
    onToggleOpen: () => void;
    onClose: () => void;
    onPromptSelect: (promptText: string) => void;
}

const SuggestedPromptsTrayItem: React.FC<SuggestedPromptsTrayItemProps> = ({
    isOpen,
    isDropUp,
    expandCategoriesByDefault,
    userPrompts,
    suggestedPromptCategories,
    savedPromptVm,
    onToggleOpen,
    onClose,
    onPromptSelect,
}) => {
    const styles = useStyles();
    const containerRef = useDismissOnOutsideClick<HTMLDivElement>(isOpen, onClose);

    return (
        <>
            <div ref={containerRef} className={styles.suggestedPromptsWrapper}>
                <Button
                    className={mergeClasses(styles.trayButton, isOpen && styles.trayButtonActive)}
                    appearance="transparent"
                    icon={<SuggestedPromptsIcon className="toolkitTrayIcon" />}
                    onClick={onToggleOpen}
                >
                    Suggested Prompts
                </Button>
                {isOpen && (
                    <SuggestedPromptsMenu
                        isDropUp={isDropUp}
                        expandCategoriesByDefault={expandCategoriesByDefault}
                        userPrompts={userPrompts}
                        suggestedPromptCategories={suggestedPromptCategories}
                        onPromptSelect={(promptText) => {
                            onPromptSelect(promptText);
                            onClose();
                        }}
                        onSaveNewPrompt={savedPromptVm.openSavePromptDialog}
                        onEditPrompt={savedPromptVm.openEditPromptDialog}
                        onDeletePrompt={savedPromptVm.openDeletePromptDialog}
                    />
                )}
            </div>
            <SavedPromptDialogs vm={savedPromptVm} />
        </>
    );
};

export default SuggestedPromptsTrayItem;
